import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  PlayCircle,
  CheckCircle,
  XCircle,
  ArrowsClockwise,
  Sparkle,
  Star,
  PaperPlaneTilt,
  Trash,
  Lightning,
} from "@phosphor-icons/react";
import { Toaster, toast } from "sonner";
import { Seo } from "../components/Seo";
import { FlycherLogo } from "../components/FlycherLogo";
import {
  getAdminToken,
  clearAdminToken,
  autoblogStatus,
  autoblogPlan,
  autoblogToggle,
  autoblogGeneratePlan,
  autoblogRunNow,
  autoblogPublishBatch,
  autoblogPublishTopic,
  autoblogSkipPlan,
  autoblogLowKdPreview,
  autoblogLowKdGenerate,
  adminDeleteBlogPost,
} from "../lib/admin";

// Tab keys used across the plan/queue view. Filtering is done in the
// browser off a single /plan fetch so switching tabs is instant.
const TAB_READY = "ready";
const TAB_UPCOMING = "upcoming";
const TAB_LOWKD = "lowkd";
const TAB_ADDTOPIC = "addtopic";

function fmtCountdown(secs) {
  if (!Number.isFinite(secs) || secs <= 0) return "now";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function fmtVolume(v) {
  if (v == null) return "—";
  if (v >= 1000) return `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k/mo`;
  return `${v}/mo`;
}

// Week number relative to the FIRST planned scheduled_date. Matches the
// screenshot's "Week 1 / Week 2 / …" chip style so the queue reads like
// an editorial calendar.
function weekLabel(scheduledDate, firstDate) {
  if (!scheduledDate || !firstDate) return "—";
  try {
    const d = new Date(scheduledDate);
    const f = new Date(firstDate);
    const days = Math.floor((d - f) / (24 * 3600 * 1000));
    return `Week ${Math.floor(days / 7) + 1}`;
  } catch {
    return "—";
  }
}

export default function AdminAutoBlog() {
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [plan, setPlan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [tab, setTab] = useState(TAB_UPCOMING);
  const [selected, setSelected] = useState(() => new Set());
  const [topicTitle, setTopicTitle] = useState("");
  const [topicAngle, setTopicAngle] = useState("");
  const [lowKdPreview, setLowKdPreview] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([autoblogStatus(), autoblogPlan()]);
      setStatus(s);
      setPlan(p.plan || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        clearAdminToken();
        nav("/admin/login", { replace: true });
      } else {
        toast.error(e?.response?.data?.detail || "Failed to load autoblog state");
      }
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    if (!getAdminToken()) {
      nav("/admin/login", { replace: true });
      return;
    }
    refresh();
  }, [refresh, nav]);

  // Load Low-KD preview lazily the first time that tab is opened.
  useEffect(() => {
    if (tab !== TAB_LOWKD || lowKdPreview) return;
    autoblogLowKdPreview()
      .then((d) => setLowKdPreview(d))
      .catch(() => setLowKdPreview({ count: 0, keywords: [] }));
  }, [tab, lowKdPreview]);

  // Derive filtered rows for each tab off a single plan[] source of truth.
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const rowsReady = useMemo(
    () =>
      plan.filter(
        (r) =>
          r.status === "planned" &&
          r.scheduled_date &&
          r.scheduled_date <= today &&
          r.priority !== "low_kd" &&
          r.priority !== "custom",
      ),
    [plan, today],
  );
  const rowsUpcoming = useMemo(
    () =>
      plan.filter(
        (r) =>
          r.status === "planned" &&
          r.scheduled_date &&
          r.scheduled_date > today &&
          r.priority !== "low_kd" &&
          r.priority !== "custom",
      ),
    [plan, today],
  );
  const rowsLowKd = useMemo(
    () =>
      plan.filter(
        (r) => r.status === "planned" && r.priority === "low_kd",
      ),
    [plan],
  );
  const rowsCustom = useMemo(
    () => plan.filter((r) => r.priority === "custom"),
    [plan],
  );
  const rowsPublished = useMemo(
    () =>
      plan
        .filter((r) => r.status === "published")
        .sort((a, b) => (b.published_at || "").localeCompare(a.published_at || "")),
    [plan],
  );

  const firstUpcomingDate = rowsUpcoming[0]?.scheduled_date;

  const activeRows =
    tab === TAB_READY
      ? rowsReady
      : tab === TAB_LOWKD
        ? rowsLowKd
        : tab === TAB_ADDTOPIC
          ? rowsCustom
          : rowsUpcoming;

  const totalPlanned = rowsReady.length + rowsUpcoming.length + rowsLowKd.length + rowsCustom.length;

  const onToggle = async () => {
    if (!status) return;
    const next = !status.enabled;
    setBusy("toggle");
    try {
      const res = await autoblogToggle(next);
      toast.success(res.enabled ? "AutoBlog ENABLED — publishes twice daily." : "AutoBlog paused.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally {
      setBusy("");
    }
  };

  const onGeneratePlan = async () => {
    if (!window.confirm(
      "Generate a fresh 120-day editorial plan? Costs ~₹1 of AI credit and REPLACES existing planned rows.",
    )) return;
    setBusy("plan");
    try {
      const res = await autoblogGeneratePlan();
      toast.success(`Planned ${res.count || 0} posts across the calendar.`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Plan generation failed");
    } finally {
      setBusy("");
    }
  };

  const onGenerateLowKd = async () => {
    if (!window.confirm(
      "Build ~300 Low-KD topics from the 1057-keyword corpus? Costs ~₹2.40 and takes ~3 min in background.",
    )) return;
    setBusy("lowkd");
    try {
      const res = await autoblogLowKdGenerate();
      toast.success(`Seeded ${res.count || 0} Low-KD candidates. More rolling in via background task.`);
      setTimeout(refresh, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Low-KD seed failed");
    } finally {
      setBusy("");
    }
  };

  const onRunNow = async () => {
    setBusy("run");
    try {
      const res = await autoblogRunNow();
      if (res.ok) {
        toast.success(`Published: ${res.title}`);
      } else {
        toast.error(res.reason || "Nothing to publish.");
      }
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally {
      setBusy("");
    }
  };

  const onPublishSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(
      `Publish ${ids.length} selected posts? Est. cost ~₹${(ids.length * 0.2).toFixed(2)}, ~${ids.length * 35}s in background.`,
    )) return;
    setBusy("bulk");
    try {
      const res = await autoblogPublishBatch({ plan_ids: ids });
      toast.success(`Queued ${res.queued || 0} posts. Sitemap auto-updates as each finishes.`);
      setSelected(new Set());
      setTimeout(refresh, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk publish failed");
    } finally {
      setBusy("");
    }
  };

  const onPublishAllInTab = async () => {
    const ids = activeRows.map((r) => r.plan_id);
    if (ids.length === 0) return;
    if (!window.confirm(
      `Publish ALL ${ids.length} posts in "${tabLabel(tab)}"? Est. cost ~₹${(ids.length * 0.2).toFixed(2)}, ~${Math.round(ids.length * 35 / 60)} min.`,
    )) return;
    setBusy("bulk");
    try {
      const res = await autoblogPublishBatch({ plan_ids: ids });
      toast.success(`Queued ${res.queued || 0} posts.`);
      setTimeout(refresh, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish-tab failed");
    } finally {
      setBusy("");
    }
  };

  const onPublishEverything = async () => {
    if (!window.confirm(
      `Publish EVERY planned post (${totalPlanned})? This will burn ~₹${(totalPlanned * 0.2).toFixed(2)} of AI credit and take ~${Math.round(totalPlanned * 35 / 60)} min. Are you sure?`,
    )) return;
    setBusy("bulk");
    try {
      const res = await autoblogPublishBatch({ all: true });
      toast.success(`Queued ${res.queued || 0} posts across all tabs.`);
      setTimeout(refresh, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish-all failed");
    } finally {
      setBusy("");
    }
  };

  const onSkip = async (planId, title) => {
    if (!window.confirm(`Skip "${title}"? It won't be published unless you regenerate the plan.`)) return;
    try {
      await autoblogSkipPlan(planId);
      toast.success("Skipped.");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Skip failed");
    }
  };

  const onDeletePublished = async (row) => {
    if (!row.post_id) {
      toast.error("Missing post_id — cannot delete.");
      return;
    }
    if (!window.confirm(
      `Delete published post "${row.title}"?\n\n` +
      `• The article at /blog/${row.slug} will 404 for readers.\n` +
      `• It is removed from the sitemap automatically.\n` +
      `• Search-engine rankings may drop.\n\n` +
      `This cannot be undone. Continue?`,
    )) return;
    try {
      await adminDeleteBlogPost(row.post_id);
      toast.success(`Deleted "${row.title}". Sitemap updated.`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const onAddTopic = async () => {
    const title = topicTitle.trim();
    if (!title) {
      toast.error("Enter a topic title first.");
      return;
    }
    if (title.length < 8) {
      toast.error("Title too short — write a proper H1.");
      return;
    }
    setBusy("topic");
    try {
      const res = await autoblogPublishTopic({ title, angle: topicAngle.trim() });
      if (res.ok) {
        toast.success(`Queued: ${title}. Publishes in ~35s + sitemap updates.`);
      } else {
        toast.error(res.reason || "Failed to queue topic.");
      }
      setTopicTitle("");
      setTopicAngle("");
      setTimeout(refresh, 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Topic publish failed");
    } finally {
      setBusy("");
    }
  };

  const toggleRowSelect = (planId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(planId)) next.delete(planId);
      else next.add(planId);
      return next;
    });
  };

  const toggleAllInTab = () => {
    const allIds = activeRows.map((r) => r.plan_id);
    const allSelected = allIds.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) allIds.forEach((id) => next.delete(id));
      else allIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const selectedCount = selected.size;
  const selectedInTab = activeRows.filter((r) => selected.has(r.plan_id)).length;
  const allInTabSelected = activeRows.length > 0 && selectedInTab === activeRows.length;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="admin-autoblog-page">
      <Seo title="AutoBlog · Admin" path="/admin/autoblog" noindex />
      <Toaster theme="dark" position="top-center" offset={20} />

      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-slate-950/90 backdrop-blur border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-100"
              data-testid="autoblog-back-to-admin"
            >
              <ArrowLeft size={14} weight="bold" />
              Admin
            </Link>
            <span className="text-slate-600">·</span>
            <FlycherLogo size={28} theme="light" />
          </div>
          <button
            type="button"
            onClick={refresh}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-slate-700 bg-slate-900 text-slate-200 hover:border-slate-500"
            data-testid="autoblog-refresh"
          >
            <ArrowsClockwise size={12} weight="bold" />
            Refresh
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8 pb-20">
        {/* Step 1 · Status + toggle */}
        <section className="mb-8">
          <p className="text-xs uppercase tracking-[0.2em] text-amber-400 font-semibold">
            Step 1 · Engine
          </p>
          <div className="mt-3 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <h1 className="font-serif text-4xl md:text-5xl text-slate-100 leading-tight">
                AutoBlog{" "}
                <span className="italic text-amber-400">engine.</span>
              </h1>
              <p className="text-sm text-slate-400 mt-2 max-w-2xl">
                Twice-daily publisher for the &ldquo;AI travel planner&rdquo; niche.
                1,057-keyword Ubersuggest corpus. Every published post auto-appears in{" "}
                <a href="/api/sitemap.xml" target="_blank" rel="noreferrer" className="text-amber-400 underline">
                  /api/sitemap.xml
                </a>{" "}
                (no cache — GSC recrawls fresh).
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={onToggle}
                disabled={busy === "toggle" || loading || !status}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition ${
                  status?.enabled
                    ? "bg-rose-500 hover:bg-rose-400 text-slate-900"
                    : "bg-emerald-400 hover:bg-emerald-300 text-slate-900"
                } disabled:opacity-50`}
                data-testid="autoblog-toggle-btn"
              >
                {status?.enabled ? <XCircle size={16} weight="bold" /> : <CheckCircle size={16} weight="bold" />}
                {status?.enabled ? "Pause AutoBlog" : "Enable AutoBlog"}
              </button>
              <button
                type="button"
                onClick={onRunNow}
                disabled={busy !== "" || loading || (status?.plan_count ?? 0) === 0}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm bg-emerald-400 hover:bg-emerald-300 text-slate-900 transition disabled:opacity-40"
                data-testid="autoblog-run-now-btn"
              >
                <PlayCircle size={16} weight="fill" />
                {busy === "run" ? "Publishing..." : "Publish next now"}
              </button>
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            <Kpi
              label="Kill switch"
              value={status?.enabled ? "ON" : "OFF"}
              sub={status?.enabled ? "Publishing on schedule" : "Cron paused"}
              tone={status?.enabled ? "green" : "red"}
              testid="autoblog-kpi-enabled"
            />
            <Kpi
              label="Keyword corpus"
              value={status?.keyword_corpus ?? "—"}
              sub="Ubersuggest merged"
              testid="autoblog-kpi-corpus"
            />
            <Kpi
              label="Planned queue"
              value={status?.plan_count ?? "—"}
              sub={`${status?.ready_count ?? 0} ready today`}
              testid="autoblog-kpi-planned"
            />
            <Kpi
              label="Published"
              value={status?.published_count ?? "—"}
              sub={`Next run in ${fmtCountdown(status?.next_run_in_seconds)}`}
              testid="autoblog-kpi-published"
            />
          </div>
        </section>

        {/* Step 2 · Editorial calendar */}
        <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5 md:p-6">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-amber-400 font-semibold">
                Step 2 · Plan
              </p>
              <h2 className="font-serif text-2xl md:text-3xl mt-1">
                Editorial calendar — next 120 days
              </h2>
            </div>
            <button
              type="button"
              onClick={onGeneratePlan}
              disabled={busy !== "" || loading}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm bg-amber-400 hover:bg-amber-300 text-slate-900 transition disabled:opacity-50"
              data-testid="autoblog-generate-plan-btn"
            >
              <Sparkle size={16} weight="fill" />
              {busy === "plan" ? "Planning..." : "Generate 120-day plan"}
            </button>
          </div>

          {/* Tabs */}
          <div
            className="flex flex-wrap items-center gap-1 border-b border-slate-800 mb-5"
            data-testid="autoblog-tabs"
          >
            <TabBtn
              active={tab === TAB_READY}
              onClick={() => setTab(TAB_READY)}
              label="Ready"
              count={rowsReady.length}
              testid="autoblog-tab-ready"
            />
            <TabBtn
              active={tab === TAB_UPCOMING}
              onClick={() => setTab(TAB_UPCOMING)}
              label="Upcoming"
              count={rowsUpcoming.length}
              testid="autoblog-tab-upcoming"
            />
            <TabBtn
              active={tab === TAB_LOWKD}
              onClick={() => setTab(TAB_LOWKD)}
              label="Low KD (Easy Wins)"
              count={rowsLowKd.length}
              icon={Star}
              testid="autoblog-tab-lowkd"
            />
            <TabBtn
              active={tab === TAB_ADDTOPIC}
              onClick={() => setTab(TAB_ADDTOPIC)}
              label="Add Topic"
              count={rowsCustom.length}
              icon={PaperPlaneTilt}
              testid="autoblog-tab-addtopic"
            />
          </div>

          {/* Add Topic form (only rendered on that tab) */}
          {tab === TAB_ADDTOPIC && (
            <div
              className="mb-6 rounded-xl border border-amber-400/30 bg-amber-400/5 p-5"
              data-testid="autoblog-addtopic-form"
            >
              <h3 className="text-lg font-semibold text-amber-300 mb-1">
                Publish a custom topic
              </h3>
              <p className="text-sm text-slate-400 mb-4">
                Give it any H1-style title. The engine runs the same writer + image + FAQ pipeline used by the daily cron and updates the sitemap on publish.
              </p>
              <div className="grid grid-cols-1 gap-3">
                <input
                  type="text"
                  value={topicTitle}
                  onChange={(e) => setTopicTitle(e.target.value)}
                  placeholder="e.g. Bali vs Phuket for Honeymoon From India — 2026 Cost & Vibe Breakdown"
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-sm placeholder:text-slate-500 focus:border-amber-400 focus:outline-none"
                  data-testid="autoblog-topic-title"
                  maxLength={140}
                />
                <input
                  type="text"
                  value={topicAngle}
                  onChange={(e) => setTopicAngle(e.target.value)}
                  placeholder="Optional angle — e.g. 'for a first-time-abroad couple with ₹1.5L budget' (bias the intro/tone)"
                  className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-sm placeholder:text-slate-500 focus:border-amber-400 focus:outline-none"
                  data-testid="autoblog-topic-angle"
                  maxLength={200}
                />
                <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                  <p className="text-xs text-slate-500">
                    Publishes in ~35s. Est. ~₹0.20 of AI credit.
                  </p>
                  <button
                    type="button"
                    onClick={onAddTopic}
                    disabled={busy === "topic" || !topicTitle.trim()}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm bg-amber-400 hover:bg-amber-300 text-slate-900 transition disabled:opacity-40"
                    data-testid="autoblog-topic-submit"
                  >
                    <PaperPlaneTilt size={14} weight="fill" />
                    {busy === "topic" ? "Queueing..." : "Publish this topic"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Low-KD empty-state seed CTA */}
          {tab === TAB_LOWKD && rowsLowKd.length === 0 && (
            <div className="mb-5 rounded-xl border border-purple-400/30 bg-purple-400/5 p-5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-purple-300">
                  Low-KD pool is empty
                </h3>
                <p className="text-sm text-slate-400 mt-1">
                  Build ~300 easy-win topics filtered to KD&lt;20, vol≥2k from your keyword corpus.
                  {lowKdPreview?.count ? ` Corpus currently has ${lowKdPreview.count} qualifying phrases.` : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={onGenerateLowKd}
                disabled={busy !== ""}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm bg-purple-400 hover:bg-purple-300 text-slate-900 transition disabled:opacity-40"
                data-testid="autoblog-lowkd-generate-btn"
              >
                <Lightning size={14} weight="fill" />
                {busy === "lowkd" ? "Seeding..." : "Seed Low-KD pool"}
              </button>
            </div>
          )}

          {/* Bulk-publish bar */}
          {tab !== TAB_ADDTOPIC && (
            <div
              className="flex flex-wrap items-center gap-3 mb-4 text-sm"
              data-testid="autoblog-bulk-bar"
            >
              <span className="text-slate-400">Select rows to bulk-publish</span>
              <button
                type="button"
                onClick={onPublishSelected}
                disabled={busy !== "" || selectedCount === 0}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-xs bg-emerald-400 hover:bg-emerald-300 text-slate-900 transition disabled:opacity-40 disabled:bg-slate-800 disabled:text-slate-500"
                data-testid="autoblog-publish-selected"
              >
                <PlayCircle size={14} weight="fill" />
                Publish selected ({selectedCount})
              </button>
              <button
                type="button"
                onClick={onPublishAllInTab}
                disabled={busy !== "" || activeRows.length === 0}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-xs border border-slate-700 hover:border-slate-500 text-slate-200 transition disabled:opacity-40"
                data-testid="autoblog-publish-all-in-tab"
              >
                Publish all in tab ({activeRows.length})
              </button>
              <button
                type="button"
                onClick={onPublishEverything}
                disabled={busy !== "" || totalPlanned === 0}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-xs border border-slate-700 hover:border-slate-500 text-slate-200 transition disabled:opacity-40"
                data-testid="autoblog-publish-everything"
              >
                Publish everything planned ({totalPlanned})
              </button>
            </div>
          )}

          {/* Table */}
          {tab !== TAB_ADDTOPIC && (
            <div className="overflow-x-auto -mx-2 md:mx-0">
              {loading ? (
                <div className="animate-pulse space-y-2 px-2 md:px-0">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="h-14 bg-slate-800/40 rounded-lg" />
                  ))}
                </div>
              ) : activeRows.length === 0 ? (
                <div className="text-center py-12 text-slate-500 text-sm">
                  {tab === TAB_READY && "No posts ready today. Enable the engine or wait for cron."}
                  {tab === TAB_UPCOMING && "No upcoming posts. Click 'Generate 120-day plan' above."}
                  {tab === TAB_LOWKD && "No Low-KD candidates yet — click 'Seed Low-KD pool' above."}
                </div>
              ) : (
                <table className="w-full text-sm border-separate border-spacing-y-1 px-2 md:px-0">
                  <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left w-8 py-2">
                        <input
                          type="checkbox"
                          checked={allInTabSelected}
                          onChange={toggleAllInTab}
                          className="rounded border-slate-600 bg-slate-800 text-emerald-400 focus:ring-emerald-400"
                          data-testid="autoblog-select-all"
                        />
                      </th>
                      <th className="text-left py-2 pr-3">Week</th>
                      <th className="text-left py-2 pr-3">Date</th>
                      <th className="text-left py-2 pr-3">Keyword</th>
                      <th className="text-left py-2 pr-3">Search Vol</th>
                      <th className="text-left py-2 pr-3">Title</th>
                      <th className="text-left py-2 pr-3">Status</th>
                      <th className="text-right py-2 pr-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeRows.slice(0, 200).map((r) => {
                      const isSel = selected.has(r.plan_id);
                      const isLowKd = r.priority === "low_kd";
                      return (
                        <tr
                          key={r.plan_id}
                          className={`bg-slate-900/50 hover:bg-slate-900 transition-colors ${
                            isSel ? "ring-1 ring-emerald-400/50" : ""
                          }`}
                          data-testid={`autoblog-plan-row-${r.plan_id}`}
                        >
                          <td className="py-3 pl-3 rounded-l-lg">
                            <input
                              type="checkbox"
                              checked={isSel}
                              onChange={() => toggleRowSelect(r.plan_id)}
                              className="rounded border-slate-600 bg-slate-800 text-emerald-400 focus:ring-emerald-400"
                              data-testid={`autoblog-row-check-${r.plan_id}`}
                            />
                          </td>
                          <td className="py-3 pr-3">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold bg-slate-800 text-slate-200 whitespace-nowrap">
                              {tab === TAB_LOWKD ? "Easy" : weekLabel(r.scheduled_date, firstUpcomingDate)}
                            </span>
                          </td>
                          <td className="py-3 pr-3 font-mono text-xs text-slate-400 whitespace-nowrap">
                            {r.scheduled_date || "—"}
                          </td>
                          <td className="py-3 pr-3 text-slate-300 max-w-[220px] truncate" title={r.source_keyword || ""}>
                            {r.source_keyword || "—"}
                          </td>
                          <td className="py-3 pr-3 tabular-nums text-amber-400 whitespace-nowrap">
                            {fmtVolume(r.volume)}
                            {isLowKd && <Star size={12} weight="fill" className="inline ml-1 text-purple-400" />}
                          </td>
                          <td className="py-3 pr-3 text-slate-100 max-w-md truncate" title={r.title}>
                            {r.title}
                          </td>
                          <td className="py-3 pr-3 text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap">
                            {r.status}
                          </td>
                          <td className="py-3 pr-3 text-right rounded-r-lg">
                            <button
                              type="button"
                              onClick={() => onSkip(r.plan_id, r.title)}
                              className="text-[11px] uppercase tracking-wider text-slate-500 hover:text-rose-400 transition-colors"
                              data-testid={`autoblog-skip-${r.plan_id}`}
                            >
                              skip
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {activeRows.length > 200 && (
                <div className="mt-3 text-xs text-slate-500 text-center">
                  Showing first 200 of {activeRows.length} rows.
                </div>
              )}
            </div>
          )}

          {/* Custom-topic tab: list already-published custom rows */}
          {tab === TAB_ADDTOPIC && rowsCustom.length > 0 && (
            <div className="mt-2">
              <h3 className="text-sm uppercase tracking-wider text-slate-500 mb-3">
                Your recent custom topics
              </h3>
              <ul className="divide-y divide-slate-800">
                {rowsCustom.slice(0, 20).map((r) => (
                  <li
                    key={r.plan_id}
                    className="py-3 flex flex-wrap items-center gap-3 text-sm"
                    data-testid={`autoblog-custom-row-${r.plan_id}`}
                  >
                    <span
                      className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                        r.status === "published"
                          ? "bg-emerald-400/20 text-emerald-300"
                          : r.status === "error"
                            ? "bg-rose-400/20 text-rose-300"
                            : "bg-slate-800 text-slate-400"
                      }`}
                    >
                      {r.status}
                    </span>
                    {r.slug ? (
                      <Link
                        to={`/blog/${r.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-1 min-w-0 text-slate-100 hover:text-amber-300 truncate"
                      >
                        {r.title}
                      </Link>
                    ) : (
                      <span className="flex-1 min-w-0 text-slate-300 truncate">{r.title}</span>
                    )}
                    <span className="text-xs text-slate-500 font-mono whitespace-nowrap">
                      {(r.published_at || r.created_at || "").slice(0, 10)}
                    </span>
                    {r.status === "published" && r.post_id && (
                      <button
                        type="button"
                        onClick={() => onDeletePublished(r)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold text-rose-300 hover:bg-rose-500/10 hover:text-rose-200 transition-colors"
                        title="Delete this article"
                        data-testid={`autoblog-delete-custom-${r.plan_id}`}
                      >
                        <Trash size={12} weight="bold" />
                        Delete
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* Recently published */}
        {rowsPublished.length > 0 && (
          <section className="mt-8 rounded-2xl border border-slate-800 bg-slate-900/50 p-5 md:p-6" data-testid="autoblog-recent-published">
            <h2 className="font-serif text-xl md:text-2xl text-slate-100 mb-4">
              Recently published
            </h2>
            <ul className="divide-y divide-slate-800">
              {rowsPublished.slice(0, 15).map((r) => (
                <li
                  key={r.plan_id}
                  className="py-3 flex flex-wrap items-center gap-3 text-sm"
                  data-testid={`autoblog-published-row-${r.plan_id}`}
                >
                  <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-400/20 text-emerald-300">
                    published
                  </span>
                  <Link
                    to={`/blog/${r.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 min-w-0 text-slate-100 hover:text-amber-300 truncate"
                    title={r.title}
                    data-testid={`autoblog-published-link-${r.slug}`}
                  >
                    {r.title}
                  </Link>
                  <span className="text-xs text-slate-500 font-mono whitespace-nowrap">
                    {r.published_at?.slice(0, 10)}
                  </span>
                  <button
                    type="button"
                    onClick={() => onDeletePublished(r)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold text-rose-300 hover:bg-rose-500/10 hover:text-rose-200 transition-colors"
                    title="Delete this article — removes from /blog and sitemap"
                    data-testid={`autoblog-delete-published-${r.plan_id}`}
                  >
                    <Trash size={12} weight="bold" />
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Last-error footer for debugging */}
        {status?.last_error && (
          <div
            className="mt-6 text-xs bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2 text-rose-300 font-mono"
            data-testid="autoblog-last-error"
          >
            Last error: {status.last_error}
          </div>
        )}
      </main>
    </div>
  );
}

function tabLabel(t) {
  return t === TAB_READY
    ? "Ready"
    : t === TAB_UPCOMING
      ? "Upcoming"
      : t === TAB_LOWKD
        ? "Low KD"
        : "Add Topic";
}

function TabBtn({ active, onClick, label, count, icon: Icon, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`relative inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors ${
        active ? "text-amber-400" : "text-slate-400 hover:text-slate-200"
      }`}
    >
      {Icon && <Icon size={14} weight={active ? "fill" : "regular"} />}
      {label}
      {count > 0 && (
        <span className={`ml-1 px-2 py-0.5 rounded-full text-[10px] font-mono ${
          active ? "bg-amber-400/20 text-amber-300" : "bg-slate-800 text-slate-400"
        }`}>
          {count}
        </span>
      )}
      {active && (
        <span className="absolute inset-x-2 -bottom-px h-0.5 bg-amber-400 rounded-full" />
      )}
    </button>
  );
}

function Kpi({ label, value, sub, tone, testid }) {
  const toneClass =
    tone === "green"
      ? "text-emerald-400"
      : tone === "red"
        ? "text-rose-400"
        : "text-slate-100";
  return (
    <div
      className="bg-slate-900/50 border border-slate-800 rounded-xl p-4"
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
        {label}
      </div>
      <div className={`text-2xl md:text-3xl font-semibold ${toneClass}`}>
        {typeof value === "number" ? value.toLocaleString?.() ?? value : value ?? "—"}
      </div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
